import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { DataTableDirective } from 'angular-datatables';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription } from 'rxjs';
import { GeneralService } from 'src/app/Service/general.service';

interface LandRecordOrderFilters {
    q: string;
    dateFrom: string;
    dateTo: string;
    payment: string;
    receipt: string;
}

/**
 * User Module → Land Record Orders
 *
 * Every Land Record order placed on jahernotice.com (tblUserAdhocDelivery), across
 * all users, with filters and server-side paging. "Generate Receipt" opens the
 * existing Add Receipt page prefilled from the order; after the receipt is saved
 * the CSR is returned here (see AddReceiptComponent → ReceiptReturnTo).
 */
@Component({
    selector: 'app-land-record-orders',
    templateUrl: './land-record-orders.component.html',
    styleUrls: ['./land-record-orders.component.scss'],
})
export class LandRecordOrdersComponent implements OnInit, OnDestroy {
    /** localStorage key telling Add Receipt where to go back to after saving. */
    static readonly RETURN_KEY = 'ReceiptReturnTo';
    static readonly RETURN_VALUE = 'land-record-orders';

    breadCrumbItems: Array<{ label: string; path: string; active?: boolean }>;
    filters: FormGroup;
    dtOptions: DataTables.Settings = {};
    dtTrigger = new Subject<any>();
    @ViewChild(DataTableDirective, { static: false })
    private datatableElement: DataTableDirective;
    baseURL: string;
    isLoading = false;
    totalCount = 0;

    /* Only the newest list request may render (a slow older page/filter response
       must not overwrite the current one). */
    private listReqSeq = 0;
    private listSub: Subscription;

    constructor(
        private gs: GeneralService,
        private router: Router,
        private toastr: ToastrService,
        private datepipe: DatePipe
    ) {}

    ngOnInit() {
        this.baseURL = this.gs.getBaseurl();
        this.breadCrumbItems = [
            { label: 'User Module', path: '/users/users-users' },
            { label: 'Land Record Orders', path: '/users/land-record-orders', active: true },
        ];
        /* Filters applied to the grid. First visit in a browser session: date
           range = TODAY on both ends, so opening the page runs a small indexed
           query instead of scanning every order ever placed. Afterwards the
           last APPLIED filters are restored (sessionStorage), so leaving the
           page and coming back shows the same list. */
        this.appliedFilters = this.restoreFilters() || this.defaultFilters();
        this.filters = new FormGroup({
            q: new FormControl(this.appliedFilters.q),
            dateFrom: new FormControl(this.appliedFilters.dateFrom),
            dateTo: new FormControl(this.appliedFilters.dateTo),
            payment: new FormControl(this.appliedFilters.payment),
            receipt: new FormControl(this.appliedFilters.receipt),
        });
        this.buildTable();
    }

    /* ── filter state ────────────────────────────────────────────────────── */

    /** The filters the grid is currently showing (what the last Search applied). */
    appliedFilters: LandRecordOrderFilters;
    private static readonly STORAGE_KEY = 'landRecordOrdersFilters';

    private defaultFilters(): LandRecordOrderFilters {
        const today = this.datepipe.transform(new Date(), 'yyyy-MM-dd');
        return { q: '', dateFrom: today, dateTo: today, payment: 'all', receipt: 'all' };
    }

    private restoreFilters(): LandRecordOrderFilters {
        try {
            const saved = JSON.parse(sessionStorage.getItem(LandRecordOrdersComponent.STORAGE_KEY) || 'null');
            if (saved && typeof saved === 'object') {
                return this.normalizeFilters(saved);
            }
        } catch (e) {
            /* malformed value → start from defaults */
        }
        return null;
    }

    private normalizeFilters(v: any): LandRecordOrderFilters {
        return {
            q: (v.q || '').trim(),
            dateFrom: v.dateFrom || '',
            dateTo: v.dateTo || '',
            payment: ['paid', 'unpaid'].indexOf(v.payment) !== -1 ? v.payment : 'all',
            receipt: ['generated', 'pending'].indexOf(v.receipt) !== -1 ? v.receipt : 'all',
        };
    }

    /** Both dates or none — a half-open range is not allowed. */
    get dateError(): string {
        const v = this.filters.value;
        if (!!v.dateFrom !== !!v.dateTo) {
            return 'Please select both Order Date From and Order Date To.';
        }
        if (v.dateFrom && v.dateTo && v.dateFrom > v.dateTo) {
            return 'Order Date To must be on or after Order Date From.';
        }
        return null;
    }

    /** Form differs from what the grid currently shows. */
    get isDirty(): boolean {
        const f = this.normalizeFilters(this.filters.value);
        const a = this.appliedFilters;
        return (
            f.q !== a.q ||
            f.dateFrom !== a.dateFrom ||
            f.dateTo !== a.dateTo ||
            f.payment !== a.payment ||
            f.receipt !== a.receipt
        );
    }

    /** Search is enabled only for a changed AND valid filter set. */
    get canSearch(): boolean {
        return this.isDirty && !this.dateError;
    }

    /** Reset is enabled whenever the form or the grid holds anything other than the blank filters. */
    get canReset(): boolean {
        const f = this.normalizeFilters(this.filters.value);
        const blank = { q: '', dateFrom: '', dateTo: '', payment: 'all', receipt: 'all' };
        const same = (x: LandRecordOrderFilters) =>
            x.q === blank.q && x.dateFrom === blank.dateFrom && x.dateTo === blank.dateTo &&
            x.payment === blank.payment && x.receipt === blank.receipt;
        return !same(f) || !same(this.appliedFilters);
    }

    ngAfterViewInit() {
        this.dtTrigger.next();
        const self = this;
        if (this.datatableElement) {
            this.datatableElement.dtInstance.then((table) => {
                $('table.land-record-orders tbody').on('click', '.details-control', function () {
                    const tr = $(this).closest('tr');
                    const row = table.row(tr);
                    if (row.child.isShown()) {
                        row.child.hide();
                        tr.removeClass('shown');
                    } else {
                        row.child(self.format(row.data()), 'lro-details-row').show();
                        tr.addClass('shown');
                    }
                });
            });
        }
    }

    ngOnDestroy() {
        if (this.listSub) {
            this.listSub.unsubscribe();
        }
        this.dtTrigger.unsubscribe();
    }

    /* ── filters ─────────────────────────────────────────────────────────── */

    /** Search: apply the form to the grid (only when changed and valid). */
    applyFilters() {
        if (!this.canSearch) {
            return;
        }
        this.commit(this.normalizeFilters(this.filters.value));
    }

    /** Reset: blank search, blank dates, all payments, All receipts — and reload. */
    resetFilters() {
        const blank: LandRecordOrderFilters = { q: '', dateFrom: '', dateTo: '', payment: 'all', receipt: 'all' };
        this.filters.reset(blank);
        this.commit(blank);
    }

    /** Receipt status toggle is a quick filter — applies immediately. */
    setReceiptFilter(value: string) {
        this.filters.controls.receipt.setValue(value);
        this.commit(Object.assign({}, this.appliedFilters, { receipt: value }));
    }

    private commit(filters: LandRecordOrderFilters) {
        this.appliedFilters = filters;
        try {
            sessionStorage.setItem(LandRecordOrdersComponent.STORAGE_KEY, JSON.stringify(filters));
        } catch (e) {
            /* storage unavailable → filters simply won't survive navigation */
        }
        this.rerender();
    }

    private rerender() {
        if (!this.datatableElement) {
            return;
        }
        this.datatableElement.dtInstance.then((table) => {
            table.ajax.reload(null, true);
        });
    }

    /* ── grid ────────────────────────────────────────────────────────────── */

    /* Page position survives navigation too (Generate Receipt → Add Receipt → back
       lands on the same page). Stored next to the filters, per browser session. */
    private static readonly PAGING_KEY = 'landRecordOrdersPaging';

    private restorePaging(): { start: number; length: number } {
        try {
            const saved = JSON.parse(sessionStorage.getItem(LandRecordOrdersComponent.PAGING_KEY) || 'null');
            const length = saved && Number(saved.length) > 0 ? Number(saved.length) : 10;
            const start = saved && Number(saved.start) >= 0 ? Number(saved.start) : 0;
            return { start: start - (start % length), length };
        } catch (e) {
            return { start: 0, length: 10 };
        }
    }

    private savePaging(start: number, length: number) {
        try {
            sessionStorage.setItem(LandRecordOrdersComponent.PAGING_KEY, JSON.stringify({ start, length }));
        } catch (e) {
            /* storage unavailable → paging simply won't survive navigation */
        }
    }

    private buildTable() {
        const paging = this.restorePaging();
        this.dtOptions = {
            pagingType: 'simple_numbers',
            pageLength: paging.length,
            displayStart: paging.start,
            processing: true,
            serverSide: true,
            searching: false, // our own filter bar drives the search
            ordering: false, // newest order first, server-side
            ajax: (dataTablesParameters: any, callback) => {
                /* always the APPLIED filters — typing in the form must not change paging requests */
                const f = this.appliedFilters;
                const pageNo = Math.floor(dataTablesParameters.start / dataTablesParameters.length) + 1;
                this.savePaging(dataTablesParameters.start, dataTablesParameters.length);
                const seq = ++this.listReqSeq;
                if (this.listSub) {
                    this.listSub.unsubscribe();
                }
                this.isLoading = true;
                this.listSub = this.gs
                    .getLandRecordOrdersList({
                        PageNo: pageNo,
                        PageSize: dataTablesParameters.length,
                        q: (f.q || '').trim(),
                        dateFrom: f.dateFrom || '',
                        dateTo: f.dateTo || '',
                        payment: f.payment || 'all',
                        receipt: f.receipt || 'all',
                    })
                    .subscribe(
                        (resp: any) => {
                            if (seq !== this.listReqSeq) {
                                return;
                            }
                            this.isLoading = false;
                            this.totalCount = resp.Count || 0;
                            callback({
                                recordsTotal: resp.Count || 0,
                                recordsFiltered: resp.Count || 0,
                                data: resp.result || [],
                            });
                        },
                        (err) => {
                            if (seq !== this.listReqSeq) {
                                return;
                            }
                            this.isLoading = false;
                            this.toastr.error('Land Record orders could not be loaded');
                            callback({ recordsTotal: 0, recordsFiltered: 0, data: [] });
                        }
                    );
            },
            /* 8 columns, two of them stacked (Order = id + date, User = id + name +
               mobile) so the grid always fits the card width — no horizontal scroll. */
            columns: [
                {
                    title: '#',
                    data: null,
                    className: 'lro-center lro-sr',
                    render: (data, type, row, meta) => {
                        const info = (meta.settings as any)._iDisplayStart || 0;
                        return info + meta.row + 1;
                    },
                },
                {
                    title: 'Order',
                    data: null,
                    render: (data) =>
                        `<div class="lro-primary" title="AdhocDeliveryID ${data.AdhocDeliveryID}">${data.Order_ID || '--'}</div>` +
                        `<div class="lro-secondary">${this.formatDate(this.orderDate(data))}</div>`,
                },
                {
                    title: 'User',
                    data: null,
                    render: (data) =>
                        `<div><a class="user-link lro-user-id" title="Open View Receipts of this user" UserId="${data.UserID}" PlanId="${data.PlanID || ''}">${data.UserID}</a>` +
                        (data.UserName ? ` <span class="lro-secondary">${data.UserName}</span>` : '') +
                        `</div>` +
                        `<div class="lro-secondary">${data.UserMobile || ''}</div>`,
                },
                {
                    title: 'Services',
                    data: null,
                    render: (data) => data.services || '<span class="text-muted">--</span>',
                },
                {
                    title: 'Amount',
                    data: null,
                    render: (data) =>
                        data.total_price != null
                            ? `<span class="lro-amount">₹ ${data.total_price}</span>`
                            : '<span class="text-muted">--</span>',
                },
                {
                    title: 'Payment',
                    data: null,
                    className: 'lro-center',
                    render: (data) => {
                        if (!data.isPaid) {
                            return `<span class="badge badge-danger" title="No online payment on the order and no receipt generated yet">Unpaid</span>`;
                        }
                        const how =
                            data.paidVia === 'receipt'
                                ? `Payment recorded on receipt ${data.recieptNumber || ''}`
                                : `Paid online via ${data.payment_type || 'Razorpay'}`;
                        const txn = data.paidTransactionId ? ` (Txn ${data.paidTransactionId})` : '';
                        return `<span class="badge badge-success" title="${how}${txn}">Paid</span>`;
                    },
                },
                {
                    title: 'Receipt',
                    data: null,
                    render: (data) =>
                        data.hasReceipt
                            ? `<a class="lro-primary" style="color:#6c5ce7" href="${this.baseURL}${data.receiptPdfPath}" target="_blank" title="View Receipt PDF"><i class="mdi mdi-file-pdf-box text-danger"></i> ${data.recieptNumber}</a>` +
                              `<div class="lro-secondary">${this.formatDate(data.recieptDate)}</div>`
                            : `<span class="badge badge-secondary">Not Generated</span>`,
                },
                {
                    title: 'Action',
                    data: null,
                    className: 'lro-actions-cell',
                    render: (data) =>
                        `<div class="lro-actions">` +
                        (data.hasReceipt
                            ? `<button type="button" class="btn btn-sm btn-outline-primary user-link" title="Open View Receipts of this user" UserId="${data.UserID}" PlanId="${data.PlanID || ''}">View Receipts</button>`
                            : `<button type="button" class="btn btn-sm btn-success generate-receipt" title="Open Add Receipt prefilled from this Land Record order" AdhocDeliveryId="${data.AdhocDeliveryID}" UserId="${data.UserID}" PlanId="${data.PlanID || ''}">Generate Receipt</button>`) +
                        `<span class="view-detail-btn details-control" title="View More"><i class="mdi mdi-eye-outline"></i></span>` +
                        `</div>`,
                },
            ],
            drawCallback: () => {
                $('.generate-receipt').off('click').on('click', (e) => {
                    const el = e.currentTarget as HTMLElement;
                    this.generateReceipt(
                        el.getAttribute('AdhocDeliveryId'),
                        el.getAttribute('UserId'),
                        el.getAttribute('PlanId')
                    );
                });
                $('.user-link').off('click').on('click', (e) => {
                    const el = e.currentTarget as HTMLElement;
                    this.openUserReceipts(el.getAttribute('UserId'), el.getAttribute('PlanId'));
                });
            },
        };
    }

    /* ── actions ─────────────────────────────────────────────────────────── */

    /** Generate Receipt → Add Receipt prefilled from the order; come back here afterwards. */
    generateReceipt(adhocDeliveryId: string, userId: string, planId: string) {
        if (!adhocDeliveryId || !userId) {
            this.toastr.error('Order or user id is missing!');
            return;
        }
        localStorage.setItem('UserId', userId);
        if (planId) {
            localStorage.setItem('PlanId', planId);
        }
        localStorage.setItem('AdhocDeliveryID', String(adhocDeliveryId));
        localStorage.setItem(LandRecordOrdersComponent.RETURN_KEY, LandRecordOrdersComponent.RETURN_VALUE);
        this.router.navigate(['users/users-users-details/receipts/add'], {
            state: { UserId: userId, PlanId: planId, AdhocDeliveryID: String(adhocDeliveryId) },
        });
    }

    /** Export the currently APPLIED filters as Excel (server builds the .xlsx). */
    exportExcel() {
        if (!this.totalCount) {
            return;
        }
        const f = this.appliedFilters;
        window.open(
            this.gs.getLandRecordOrdersExportUrl({
                q: f.q,
                dateFrom: f.dateFrom,
                dateTo: f.dateTo,
                payment: f.payment,
                receipt: f.receipt,
            }),
            '_blank'
        );
    }

    /** Open the user's View Receipts page (same context the user list sets). */
    openUserReceipts(userId: string, planId: string) {
        if (!userId) {
            return;
        }
        localStorage.setItem('UserId', userId);
        if (planId) {
            localStorage.setItem('PlanId', planId);
        }
        localStorage.removeItem('AdhocDeliveryID');
        localStorage.removeItem(LandRecordOrdersComponent.RETURN_KEY);
        this.router.navigate(['users/users-users-details/receipts'], {
            state: { UserId: userId, PlanId: planId },
        });
    }

    /* ── helpers ─────────────────────────────────────────────────────────── */

    private orderDate(o: any) {
        /* Order Record Date is THE order date */
        return o.Order_Record_date || o.OrderDate || o.Order_date || null;
    }

    /* MSSQL datetimes are stored in IST but the API sends them with a 'Z' (UTC) suffix,
       so format in UTC to show the exact stored date instead of shifting it +5:30. */
    private formatDate(v: any): string {
        return this.hasValue(v) ? this.datepipe.transform(new Date(v), 'dd-MM-yyyy', 'UTC') : '--';
    }

    private hasValue(v: any): boolean {
        if (v === null || v === undefined) {
            return false;
        }
        const s = String(v).trim();
        return (
            s !== '' &&
            ['N/A', 'NA', '--', '-', 'NULL', 'UNDEFINED', '0000-00-00'].indexOf(s.toUpperCase()) === -1
        );
    }

    /** View More: only rows that have a value. */
    format(o: any): string {
        const location = [o.VillageName, o.TalukaName, o.DistrictName]
            .filter((x) => this.hasValue(x))
            .join(', ');
        const rows: [string, any][] = [
            ['Order ID', o.Order_ID],
            ['Order Date', this.hasValue(this.orderDate(o)) ? this.formatDate(this.orderDate(o)) : null],
            ['User ID', o.UserID],
            ['Full Name', o.UserName],
            ['Mobile', o.UserMobile],
            ['Email', o.UserEmail],
            ['Services Ordered', o.services],
            ['Village / Taluka / District', location],
            ['Survey No.', o.Survey_No],
            [
                'Payment',
                o.isPaid
                    ? o.paidVia === 'receipt'
                        ? `Paid — recorded on receipt ${o.recieptNumber || ''}`
                        : `Paid online via ${o.payment_type || 'Razorpay'}`
                    : 'Unpaid',
            ],
            ['Transaction ID', o.paidTransactionId],
            ['Order Amount', o.total_price],
            ['Bill to Name', o.BillTo],
            ['Billing Address', o.BillingAddress],
            ['GST No.', o.CustomerGST],
            ['Receipt No.', o.recieptNumber],
            ['Receipt Date', this.hasValue(o.recieptDate) ? this.formatDate(o.recieptDate) : null],
            ['Receipt generated by', o.receiptCreatedByName],
            ['State', o.StateName],
            ['Source', o.Source],
        ];
        const body = rows
            .filter(([, value]) => this.hasValue(value))
            .map(([label, value]) => `<tr><td>${label}:</td><td>${value}</td></tr>`)
            .join('');
        return `<table class="lro-details" cellpadding="5" cellspacing="0" border="0">${body}</table>`;
    }
}
